import { auditService } from '@/audit';
import type { PlatformEvent } from '@/platform/events/EventModels';
import { subscribeToPlatformEvents } from '@/platform/events/EventSubscriber';

function toAuditAction(
  event: PlatformEvent,
):
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'LOGIN'
  | 'LOGOUT'
  | 'EXPORT'
  | 'IMPORT'
  | 'RESET_PASSWORD'
  | 'PERMISSION_CHANGE'
  | 'ROLE_ASSIGNED'
  | 'FEATURE_FLAG_UPDATED'
  | 'PRODUCT_DEPLOYED'
  | 'API_CONFIGURED'
  | 'MENU_UPDATED'
  | 'ACCESS' {
  if (event.type.includes('CREATED')) {
    return 'CREATE';
  }

  if (event.type.includes('DELETED')) {
    return 'DELETE';
  }

  if (event.type === 'AUTH_LOGIN') {
    return 'LOGIN';
  }

  if (event.type === 'AUTH_LOGOUT') {
    return 'LOGOUT';
  }

  if (event.type === 'AUDIT_EXPORTED') {
    return 'EXPORT';
  }

  if (
    event.type === 'PERMISSION_UPDATED' ||
    event.type === 'PERMISSION_REVOKED'
  ) {
    return 'PERMISSION_CHANGE';
  }

  if (event.type === 'ROLE_ASSIGNED') {
    return 'ROLE_ASSIGNED';
  }

  if (event.type === 'FEATURE_FLAG_UPDATED') {
    return 'FEATURE_FLAG_UPDATED';
  }

  if (event.type === 'PRODUCT_DEPLOYED') {
    return 'PRODUCT_DEPLOYED';
  }

  if (event.type === 'API_CONFIGURED') {
    return 'API_CONFIGURED';
  }

  if (event.type === 'MENU_UPDATED') {
    return 'MENU_UPDATED';
  }

  if (event.type === 'ACCOUNT_ACTIVATED' || event.type === 'ACCOUNT_LOCKED') {
    return 'ACCESS';
  }

  return 'UPDATE';
}

function toResourceLabel(resource: string): string {
  return resource
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (value) => value.toUpperCase());
}

function shouldPersistAuditEvent(event: PlatformEvent): boolean {
  if (event.metadata?.auditRequired === false) {
    return false;
  }

  if (
    event.category === 'embeddedApplications' &&
    event.type === 'API_CONFIGURED'
  ) {
    return false;
  }

  return true;
}

export function registerAuditPublisher(): () => void {
  return subscribeToPlatformEvents((event) => {
    if (!shouldPersistAuditEvent(event)) {
      return;
    }

    void auditService.log({
      timestamp: event.occurredAt,
      actor: {
        id: event.actor.id,
        displayName: event.actor.displayName,
      },
      actorType: event.actor.actorType,
      action: toAuditAction(event),
      resource: toResourceLabel(event.resource),
      resourceId: event.resourceId,
      details: event.details,
      severity: event.severity,
      ipAddress: event.ipAddress,
      environment: event.environment,
      product: event.product,
      category: event.category,
      metadata: event.metadata,
      correlationId: event.correlationId,
      sessionId: event.sessionId,
    });
  });
}
